import { z } from 'zod'
import { auditFieldsSchema, uuidSchema } from './common.ts'

export const CUSTOM_FIELD_TYPES = ['text', 'number', 'date', 'select', 'checkbox'] as const

export const customFieldTypeSchema = z.enum(CUSTOM_FIELD_TYPES)
export type CustomFieldType = z.infer<typeof customFieldTypeSchema>

export const CUSTOM_FIELD_TYPE_LABELS: Record<CustomFieldType, string> = {
  text: 'Текст',
  number: 'Число',
  date: 'Дата',
  select: 'Вибір зі списку',
  checkbox: 'Прапорець',
}

export const customFieldValuesSchema = z.record(z.string(), z.unknown())
export type CustomFieldValues = z.infer<typeof customFieldValuesSchema>

const definitionFields = {
  key: z
    .string()
    .trim()
    .regex(/^[a-z][a-z0-9_]*$/)
    .max(64),
  label: z.string().trim().min(1).max(100),
  type: customFieldTypeSchema,
  options: z.array(z.string().trim().min(1)).min(1).nullable(),
  required: z.boolean(),
  position: z.number().int().nonnegative(),
}

const validateDefinitionOptions = (
  definition: { type: CustomFieldType; options?: string[] | null },
  context: z.RefinementCtx,
): void => {
  if (definition.type === 'select' && !definition.options?.length) {
    context.addIssue({
      code: 'custom',
      message: 'Select fields require at least one option',
      path: ['options'],
    })
  }
  if (
    definition.type !== 'select' &&
    definition.options !== null &&
    definition.options !== undefined
  ) {
    context.addIssue({
      code: 'custom',
      message: 'Only select fields can define options',
      path: ['options'],
    })
  }
}

export const customFieldDefinitionSchema = z
  .object({
    id: uuidSchema,
    entity: z.literal('deal'),
    ...definitionFields,
  })
  .extend(auditFieldsSchema.shape)

export type CustomFieldDefinition = z.infer<typeof customFieldDefinitionSchema>

export const createCustomFieldDefinitionInputSchema = z
  .strictObject({
    ...definitionFields,
    options: definitionFields.options.default(null),
    required: definitionFields.required.default(false),
    position: definitionFields.position.default(0),
  })
  .superRefine(validateDefinitionOptions)

export type CreateCustomFieldDefinitionInput = z.infer<
  typeof createCustomFieldDefinitionInputSchema
>

export const updateCustomFieldDefinitionInputSchema = z
  .strictObject(definitionFields)
  .partial()
  .refine((input) => Object.keys(input).length > 0, 'At least one field must be updated')

export type UpdateCustomFieldDefinitionInput = z.infer<
  typeof updateCustomFieldDefinitionInputSchema
>

export const updateDealCustomFieldsInputSchema = z.strictObject({
  customFields: customFieldValuesSchema,
})

export type UpdateDealCustomFieldsInput = z.infer<typeof updateDealCustomFieldsInputSchema>

const valueSchemaFor = (definition: CustomFieldDefinition): z.ZodType => {
  switch (definition.type) {
    case 'text':
      return z.string()
    case 'number':
      return z.number().finite()
    case 'date':
      return z.iso.date()
    case 'select':
      return z.string().refine((value) => definition.options?.includes(value) ?? false)
    case 'checkbox':
      return z.boolean()
  }
}

export const buildCustomFieldSchema = (definitions: readonly CustomFieldDefinition[]) => {
  const shape: Record<string, z.ZodType> = {}
  for (const definition of definitions) {
    const valueSchema = valueSchemaFor(definition)
    shape[definition.key] = definition.required ? valueSchema : valueSchema.optional()
  }
  return z.strictObject(shape)
}
